import * as _ from 'lodash';

import { applicationLogout, notificationReceived } from '../../common';
import { ISignalrService } from './signalr.service';
import { Notification, NotificationType, OnlineUserDto } from '../../models';
import { IStoreService } from '../store.service';
import { IConfigurationService } from './configuration.service';

export interface INotificationHubService {
    connect(): angular.IPromise<void>;
    disconnect(): angular.IPromise<void>;
    isConnected(): boolean;
    isConnectionConfigured(): boolean;
    getConnectionState(): number;
    reset(): void;
    start(): angular.IPromise<void>;
}

export class NotificationHubService implements INotificationHubService {
    static serviceId = 'notificationHubService';

    static $inject = [
        '$q',
        '$rootScope',
        'signalrService',
        'storeService',
        'configurationService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$rootScope: angular.IRootScopeService,
        private _signalrService: ISignalrService,
        private _storeService: IStoreService,
        private _configurationService: IConfigurationService
    ) {
        _$rootScope.$on(applicationLogout, () => this.disconnect());
    }

    connect(): angular.IPromise<void> {
        const result = this._$q.defer<void>();

        if (!this.isConnected()) {
            // Proxy to hub seems to be cleared on every disconnection
            // so we just add a new one with a fire & forget
            const proxy = this._signalrService.connection.createHubProxy('notificationHub');
            proxy.on('push', notification => this.pushNotification(notification));
            this._signalrService.connect()
                .then(() => result.resolve())
                .catch(() => result.reject());
        }
        else
        {
            result.resolve();
        }

        return result.promise;
    }

    disconnect(): angular.IPromise<void> {
        return this.isConnected()
            ? this._$q.when(this._signalrService.disconnect())
            : this._$q.when();
    }

    isConnected(): boolean {
        return this._signalrService.isConnected();
    }

    isConnectionConfigured(): boolean {
        return !!this._signalrService.connection.url;
    }

    getConnectionState(): number {
        return this._signalrService.connection.state;
    }

    reset(): void {
        this._configurationService.loadData();
    }

    start(): angular.IPromise<void> {
        return this._signalrService.start();
    }

    private pushNotification(notification: Notification) {
        const converted: Notification = _.mapKeys(notification, (v, k) => _.camelCase(k as any)) as any;
        converted.payload = _.mapKeys(converted.payload, (v, k) => _.camelCase(k as any)) as any;
        converted.workEnvironment = _.mapKeys(converted.workEnvironment, (v, k) => _.camelCase(k as any)) as any;
        this._$rootScope.$broadcast(notificationReceived, converted);
        this.notifyStore(converted);
    }

    private notifyStore(notification: Notification) {
        switch (notification.type) {
            case NotificationType.OnlineUser:
                const { payload } = notification;
                const onlineUser = this.camelize(payload['onlineUser']) as OnlineUserDto;
                const isRemove = payload['isRemove'] as boolean;
                this._storeService.dispatchers().onlineUsers.updateOne(onlineUser, isRemove);
                break;
            default:
                break;
        }
    }

    private camelize(obj) {
        return Array.isArray(obj)
            ? obj.map(v => this.camelize(v))
            : _.isPlainObject(obj)
                ? Object.keys(obj).reduce((result, key) => ({ ...result, [_.camelCase(key)]: this.camelize(obj[key]), }), {},)
                : obj;
    }
}